import React, { FunctionComponent, memo, useMemo } from "react";
import { useIntl } from "react-intl";
import { zip, sum, isEqual } from "lodash";

import { ElectroPrices, GasPrices, PredictionData } from "./types";

import GroupHeading from "./GroupHeading";
import RowOfValues from "./RowOfValues";



interface IProps {
  cellWidth?: number;
  cellPaddingX?: number;
  data: Record<string, any>;
}


const RowsOfValues: FunctionComponent<IProps> = memo(function RowsOfValues({
  cellWidth = 35,
  cellPaddingX = 0,
  data,
}) {
  const intl = useIntl();
  const prediction: PredictionData = data.prediction;
  const e_prices: ElectroPrices = data.e_prices;
  const g_prices: GasPrices = data.g_prices;
  const nowHour = useMemo<number>(() => prediction?.hour || 0, [prediction?.hour]);
  const nowHourIndex = useMemo<number>(() => prediction?.hours?.indexOf(nowHour), [nowHour, prediction?.hours]);

  const consumptionPrediction = useMemo(() => prediction ? zip(prediction.predicted_consumption, prediction.predicted_consumption_heating).map(sum) : [], [prediction]);

  const commonRowProps = useMemo(() => ({
    cellWidth,
    cellPaddingX,
    nowHourIndex,
  }), [cellWidth, cellPaddingX, nowHourIndex]);

  const goodBadColors = useMemo(() => ({
    // colors: ["#3ab860", "#E309"],
    colors: ["#3ab860", "transparent"],
  }), []);

  const badGoodColors = useMemo(() => ({
    // colors: ["#E309", "#3ab860"],
    colors: ["transparent", "#3ab860"],
  }), []);

  return (
    <>
      {prediction && <>
        <GroupHeading>
          {intl.formatMessage({
            id: "site__trading__automat__heading",
            defaultMessage: "Automat",
          })}
        </GroupHeading>
        <RowOfValues {...commonRowProps} {...badGoodColors} key="dc" label={intl.formatMessage({id: "site__trading__row__discharge_plan", defaultMessage: "charge plan"})} units="%" values={prediction.discharge_plan.map(_ => -_ * 100)} signedValues />
        <RowOfValues {...commonRowProps} key="cg" label={intl.formatMessage({id: "site__trading__row__no_charge", defaultMessage: "no charging"})} values={prediction.no_charge} withoutChart />
        <RowOfValues {...commonRowProps} key="dg" label={intl.formatMessage({id: "site__trading__row__no_discharge", defaultMessage: "no discharging"})} values={prediction.no_discharge} withoutChart />
        <RowOfValues {...commonRowProps} key="cp" label={intl.formatMessage({id: "site__trading__row__no_chp", defaultMessage: "no CHP"})} values={prediction.no_chp} withoutChart />
        <RowOfValues {...commonRowProps} {...badGoodColors} key="hp" label={intl.formatMessage({id: "site__trading__row__heating_plan", defaultMessage: "heating plan"})} units="kWh" values={prediction.heating_plan} />
        <RowOfValues {...commonRowProps} key="he" label={intl.formatMessage({id: "site__trading__row__heating_electric", defaultMessage: "→electricity"})} units="kW" values={prediction.heating_electric_power} withoutChart />
        <RowOfValues {...commonRowProps} key="hg" label={intl.formatMessage({id: "site__trading__row__heating_gas", defaultMessage: "→gas"})} units="kW" values={prediction.heating_gas_power} withoutChart />
        <RowOfValues {...commonRowProps} key="hh" label={intl.formatMessage({id: "site__trading__row__no_heating", defaultMessage: "no heating"})} values={prediction.no_heating} withoutChart />

        {e_prices && <>
          <GroupHeading>
            {intl.formatMessage({
              id: "site__trading__electricity_prices__heading",
              defaultMessage: "Electricity Prices",
            })}
          </GroupHeading>
          <RowOfValues {...commonRowProps} {...goodBadColors} key="ey" label={intl.formatMessage({id: "site__trading__row__buy", defaultMessage: "buy"})} units="€/MWh" values={e_prices.buy_kwh} />
          <RowOfValues {...commonRowProps} key="eb" label={intl.formatMessage({id: "site__trading__row__base", defaultMessage: "base"})} units="€/MWh" values={e_prices.base_kwh} withoutChart />
          <RowOfValues {...commonRowProps} key="es" label={intl.formatMessage({id: "site__trading__row__sell", defaultMessage: "sell"})} units="€/MWh" values={e_prices.sell_kwh} withoutChart />
        </>}

        {g_prices && <>
          <GroupHeading>
            {intl.formatMessage({
              id: "site__trading__gas_prices__heading",
              defaultMessage: "Gas Prices",
            })}
          </GroupHeading>
          <RowOfValues {...commonRowProps} {...goodBadColors} key="gy" label={intl.formatMessage({id: "site__trading__row__buy", defaultMessage: "buy"})} units="€/MWh" values={g_prices.buy_kwh} />
          <RowOfValues {...commonRowProps} key="gb" label={intl.formatMessage({id: "site__trading__row__base", defaultMessage: "base"})} units="€/MWh" values={g_prices.base_kwh} withoutChart />
          <RowOfValues {...commonRowProps} key="ge" label={intl.formatMessage({id: "site__trading__row__in_electricity", defaultMessage: "→electricity"})} units="€/MWh" values={g_prices.electricity_kwh} withoutChart />
          <RowOfValues {...commonRowProps} key="gh" label={intl.formatMessage({id: "site__trading__row__in_heat", defaultMessage: "→heat"})} units="€/MWh" values={g_prices.heat_kwh} withoutChart />
        </>}

        <GroupHeading>
          {intl.formatMessage({
            id: "site__trading__prediction__heading",
            defaultMessage: "Prediction",
          })}
        </GroupHeading>
        <RowOfValues {...commonRowProps} {...goodBadColors} key="cs" label={intl.formatMessage({id: "site__trading__row__clouds", defaultMessage: "clouds"})} units="%" values={prediction.weather.map(_ => (1 - _) * 100)} />
        <RowOfValues {...commonRowProps} {...badGoodColors} key="pp" label={intl.formatMessage({id: "site__trading__row__production__pv", defaultMessage: "production PV"})} units="kWh" values={prediction.predicted_production_pv} corrections={prediction.real_production_pv} />
        <RowOfValues {...commonRowProps} {...badGoodColors} key="pc" label={intl.formatMessage({id: "site__trading__row__production__chp", defaultMessage: "production CHP"})} units="kWh" values={prediction.predicted_production_chp} corrections={prediction.real_production_chp} />
        <RowOfValues {...commonRowProps} key="gc" label={intl.formatMessage({id: "site__trading__row__advantage_gas_to_consume", defaultMessage: "→to consume"})} values={prediction.consumption_bound_advantage_gas} withoutChart />
        <RowOfValues {...commonRowProps} key="gs" label={intl.formatMessage({id: "site__trading__row__advantage_gas_to_sell", defaultMessage: "→to sell"})} values={prediction.market_bound_advantage_gas} withoutChart />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="et" label={intl.formatMessage({id: "site__trading__row__carbon_emissions", defaultMessage: "CO<sub>2</sub> emissions"})} units="kg/kWh" values={prediction.predicted_carbon_emissions} corrections={prediction.real_carbon_emissions} multiplier={0.001} />
        <RowOfValues {...commonRowProps} key="ep" label={intl.formatMessage({id: "site__trading__row__carbon_emissions_pv", defaultMessage: "→PV"})} units="kg/kWh" values={prediction.predicted_carbon_emissions_pv} corrections={prediction.real_carbon_emissions_pv} multiplier={0.001} withoutChart />
        <RowOfValues {...commonRowProps} key="ec" label={intl.formatMessage({id: "site__trading__row__carbon_emissions_chp", defaultMessage: "→CHP"})} units="kg/kWh" values={prediction.predicted_carbon_emissions_chp} corrections={prediction.real_carbon_emissions_chp} multiplier={0.001} withoutChart />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="es" label={intl.formatMessage({id: "site__trading__row__carbon_savings", defaultMessage: "CO<sub>2</sub> savings"})} units="kg/kWh" values={prediction.predicted_carbon_savings} corrections={prediction.real_carbon_savings} multiplier={0.001} />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="co" label={intl.formatMessage({id: "site__trading__row__consumption", defaultMessage: "consumption"})} units="kWh" values={consumptionPrediction} corrections={prediction.real_consumption} />
        <RowOfValues {...commonRowProps} key="cf" label={intl.formatMessage({id: "site__trading__row__consumption_furnace", defaultMessage: "→furnace"})} units="kWh" values={prediction.predicted_consumption_furnace} corrections={prediction.real_consumption_furnace} withoutChart />
        <RowOfValues {...commonRowProps} key="ch" label={intl.formatMessage({id: "site__trading__row__consumption_heating", defaultMessage: "→heating"})} units="kWh" values={prediction.predicted_consumption_heating} withoutChart />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="so" label={intl.formatMessage({id: "site__trading__row__needed_soc", defaultMessage: "needed SoC"})} units="%" values={prediction.predicted_soc} corrections={prediction.real_soc} />
        <RowOfValues {...commonRowProps} {...goodBadColors} key="ex" label={intl.formatMessage({id: "site__trading__row__power_draw", defaultMessage: "grid power draw"})} units="kWh" values={prediction.predicted_power_draw} corrections={prediction.real_power_draw} />
      </>}
    </>
  );
}, isEqual);

export default RowsOfValues;
